import React from "react";
import { useGameIdFromRoute } from "../games/WithGameIdFromRoute";
import { StaticListItemUrl } from "../core/EditableListItemUrl";
import { shortUrl, shortUrlFeedback, shortUrlPnp } from "../games/model";
import { useGame, useGameSlugs, useMyProfile, useRemoveSlugToGame } from "@seabrookstudios/pitch2table";
import Skeleton from "@mui/material/Skeleton";
import Delete from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemButton from "@mui/material/ListItemButton";
import ArrowForwardOutlined from "@mui/icons-material/ArrowForwardOutlined";
import { Link } from "react-router-dom";
import { Path } from "../Path";
import { DownloadQrCodeStickerIcon } from "../core/QrCode";
import Stack from "@mui/material/Stack";
import { s2Url } from "@seabrookstudios/types";
import Feedback from "@mui/icons-material/Feedback";
import Typography from "@mui/material/Typography";
import { formatAge, formatDuration, formatPlayerCount } from "../games/formatters";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { Designers } from "../games/DesignerBlock";
import Tooltip from "@mui/material/Tooltip";

/**
 * @param {object} props
 * @param {string} props.item
 */
export const ListEntry = ({ item }) => {
  const gameId = useGameIdFromRoute();
  const { data: profile } = useMyProfile();
  const { data: game } = useGame(gameId);
  const removeSlug = useRemoveSlugToGame(gameId);

  if (!profile || !game) {
    return <Skeleton />;
  }

  const url = shortUrl(profile.slug, item);
  const urlFeedback = shortUrlFeedback(profile.slug, item);

  const onRemoveSlug = async () => {
    await removeSlug.mutateAsync(item);
  };

  const DeleteSlug = () => {
    return (
      <Stack direction="row">
        {game.feedbackEnabled ? (
          <DownloadQrCodeStickerIcon
            url={urlFeedback}
            filename={`pitch2table-qr-${item}-feedback.png`}
            Icon={Feedback}
            title="Feedback QR"
            subtitle="Use this QR code to link to the feedback page for this game."
            cta="Scan QR to give feedback"
            enabled={game.feedbackEnabled}
          >
            <Box width="100%" borderTop={1} borderColor="GrayText">
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h4">{game.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {game.internalId}
                </Typography>
              </Stack>
              <Designers game={game} />
              <Stack direction="row" justifyContent="space-between" paddingTop={2}>
                <Chip label={`Players: ${formatPlayerCount(game.playerCount)}`} />
                <Chip label={`Duration: ${formatDuration(game.duration)}`} />
                <Chip label={`Age: ${formatAge(game.age)}`} />
              </Stack>
            </Box>
          </DownloadQrCodeStickerIcon>
        ) : (
          <Tooltip title="Enable feedback to access this QR code.">
            <Box>
              <DownloadQrCodeStickerIcon
                url={urlFeedback}
                filename={`pitch2table-qr-${item}-feedback.png`}
                Icon={Feedback}
                title="Feedback QR"
                subtitle="Use this QR code to link to the feedback page for this game."
                cta="Scan QR to give feedback"
                enabled={game.feedbackEnabled}
              />
            </Box>
          </Tooltip>
        )}
        <DownloadQrCodeStickerIcon
          url={url}
          filename={`pitch2table-qr-${item}.png`}
          title="Public Game QR"
          subtitle="Use this QR code to link to the public page for this game."
          cta="Scan QR to view on pitch2table.com"
        >
          <Box width="100%" borderTop={1} borderColor="GrayText">
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h4">{game.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {game.internalId}
              </Typography>
            </Stack>
            <Designers game={game} />
            <Stack direction="row" justifyContent="space-between" paddingTop={2}>
              <Chip label={`Players: ${formatPlayerCount(game.playerCount)}`} />
              <Chip label={`Duration: ${formatDuration(game.duration)}`} />
              <Chip label={`Age: ${formatAge(game.age)}`} />
            </Stack>
          </Box>
        </DownloadQrCodeStickerIcon>
        <ListItemButton onClick={onRemoveSlug}>
          {removeSlug.isPending ? <CircularProgress size={20} /> : <Delete />}
        </ListItemButton>
      </Stack>
    );
  };

  return <StaticListItemUrl text={s2Url(url)} title={item} Button={DeleteSlug} />;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.item
 */
export const ListEntryPnpSlugs = ({ item }) => {
  const { data: profile } = useMyProfile();
  const { data: slugs = [] } = useGameSlugs(item.gameId);

  const GoToPublisher = () => {
    return (
      <ListItemButton component={Link} to={Path.game(item.gameId).publisher(item.publisherId)}>
        <ArrowForwardOutlined />
      </ListItemButton>
    );
  };

  if (!profile || slugs.length === 0) {
    return <Skeleton />;
  }

  return (
    <StaticListItemUrl
      text={s2Url(shortUrlPnp(profile.slug, slugs[0], item.pnp))}
      title={item.publisherName}
      Button={GoToPublisher}
    />
  );
};
